export const isHtmlEffectivelyEmpty = (value) => {
  if (typeof value !== 'string') return true;
  const normalized = value
    .replace(/<br\s*\/?>(?=\s*<\/p>)/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, '')
    .toLowerCase();
  return normalized === '' || normalized === '<p></p>';
};

export const hasMeaningfulEditorJson = (node) => {
  const visit = (value) => {
    if (!value || typeof value !== 'object') return false;

    if (typeof value.text === 'string' && value.text.replace(/\u00a0/g, ' ').trim() !== '') {
      return true;
    }

    const type = String(value.type || '');
    const isNeutralNode = ['doc', 'paragraph', 'text', 'hardBreak'].includes(type);
    const children = Array.isArray(value.content) ? value.content : [];

    for (const child of children) {
      if (visit(child)) return true;
    }

    if (!isNeutralNode && children.length === 0) {
      return true;
    }

    return false;
  };

  return visit(node);
};

export const hasJsonContent = (value, options = {}) => {
  const allowChunkRefs = Boolean(options?.allowChunkRefs);
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value.chunks)) return allowChunkRefs && value.chunks.length > 0;
  if (value.type === 'doc') return hasMeaningfulEditorJson(value);
  return false;
};

export const isMetadataOnlyChunkRefsContent = (value) => {
  if (!value || typeof value !== 'object') return false;
  if (!Array.isArray(value.chunks)) return false;
  const type = String(value?.type || '').trim().toLowerCase();
  if (type === 'doc') return false;
  return value.chunks.length > 0;
};

export const isIncomingContentEmpty = (content) => {
  if (content == null) return true;
  if (typeof content === 'string') return isHtmlEffectivelyEmpty(content);

  if (typeof content === 'object') {
    const isWrapped =
      Object.prototype.hasOwnProperty.call(content, 'json') &&
      Object.prototype.hasOwnProperty.call(content, 'html');

    if (isWrapped) {
      return !hasJsonContent(content.json) && isHtmlEffectivelyEmpty(content.html);
    }

    if (content.type === 'doc' || Array.isArray(content.chunks)) {
      return !hasJsonContent(content);
    }
  }

  return false;
};

export const hasPersistedDocContent = (docRecord) => {
  if (!docRecord || typeof docRecord !== 'object') return false;
  return hasJsonContent(docRecord.content_json, { allowChunkRefs: true });
};

export const shouldBlockDestructiveSave = (incomingContent, persistedDocRecord) => (
  isIncomingContentEmpty(incomingContent) && hasPersistedDocContent(persistedDocRecord)
);

export const normalizeToEditorContent = ({ rawContent, rawContentJson } = {}) => {
  if (
    rawContent &&
    typeof rawContent === 'object' &&
    Object.prototype.hasOwnProperty.call(rawContent, 'json') &&
    Object.prototype.hasOwnProperty.call(rawContent, 'html')
  ) {
    return {
      content: rawContent,
      classification: 'wrapped'
    };
  }

  if (rawContent && typeof rawContent === 'object' && rawContent.type === 'doc') {
    return {
      content: {
        json: rawContent,
        html: ''
      },
      classification: 'doc-json'
    };
  }

  if (rawContentJson && typeof rawContentJson === 'object') {
    return {
      content: {
        json: rawContentJson,
        html: typeof rawContent === 'string' ? rawContent : ''
      },
      classification: 'content-json'
    };
  }

  if (rawContent !== undefined && rawContent !== null) {
    return {
      content: rawContent,
      classification: typeof rawContent === 'string' ? 'html-string' : 'raw-object'
    };
  }

  return {
    content: null,
    classification: 'empty'
  };
};

export const isEditorContentEmpty = (content) => {
  if (content == null) return true;

  if (typeof content === 'string') {
    return isHtmlEffectivelyEmpty(content) || content.trim() === '';
  }

  if (typeof content === 'object') {
    if (
      Object.prototype.hasOwnProperty.call(content, 'json') &&
      Object.prototype.hasOwnProperty.call(content, 'html')
    ) {
      const jsonPart = content.json;
      const htmlPart = content.html;
      const jsonEmpty = !hasMeaningfulEditorJson(jsonPart);
      const htmlEmpty = isHtmlEffectivelyEmpty(typeof htmlPart === 'string' ? htmlPart : '');
      return jsonEmpty && htmlEmpty;
    }

    if (content.type === 'doc') {
      return !hasMeaningfulEditorJson(content);
    }
  }

  return false;
};
